/**
 * Shopify Forms — shadow DOM style injection.
 *
 * Shopify Forms renders its form inside a Shadow DOM, which blocks external
 * CSS from styling the internal submit button, hover states, or reordering
 * the marketing-consent checkbox. This script reaches into the shadow root
 * via JS (the only way) and injects a <style> element that:
 *
 *   1. Restyles the submit button to match the theme's primary .button
 *      (background, hover, shine sweep, uppercase, padding).
 *   2. Reorders the marketing-consent checkbox to appear AFTER the message
 *      field (Shopify Forms hardcodes it after Email).
 *
 * Selectors use stable [data-testid] attributes and [name] attributes,
 * NOT the hashed CSS-module class names (_formSubmitButton_cit2d_96 etc.)
 * which change between Shopify Forms releases.
 *
 * BRITTLENESS NOTE: Shopify Forms is a closed component. If they change
 * the shadow DOM structure or remove these data-testid attributes, this
 * script may silently stop working. Sanity-check the contact form after
 * any Shopify Forms app update.
 */
(function () {
  var SHADOW_CSS = [
    /* ---- Primary submit button — replicate theme .button ----
       All properties use !important + multi-attribute selector to
       beat Shopify Forms' internal CSS module hashes on specificity. */
    /* Parent selector form[data-testid="form"] > ... boosts specificity
       to beat any Shopify Forms !important rules. */
    'form[data-testid="form"] > button[data-testid="btn-form-submit"][type="submit"] {',
    '  position: relative !important;',
    '  overflow: hidden !important;',
    '  isolation: isolate !important;',
    '  background: #2E69A7 !important;',
    '  background-color: #2E69A7 !important;',
    '  background-image: none !important;',
    '  color: #ffffff !important;',
    '  border: none !important;',
    '  border-radius: 0 !important;',
    '  padding: 0.75rem 2rem !important;',
    '  text-transform: uppercase !important;',
    '  font-weight: 500 !important;',
    '  letter-spacing: 0.05em !important;',
    '  transition: background-color 200ms ease-out !important;',
    '  cursor: pointer !important;',
    '  opacity: 1 !important;',
    '  filter: none !important;',
    '}',
    'form[data-testid="form"] > button[data-testid="btn-form-submit"][type="submit"]:hover,',
    'form[data-testid="form"] > button[data-testid="btn-form-submit"][type="submit"]:focus,',
    'form[data-testid="form"] > button[data-testid="btn-form-submit"][type="submit"]:active {',
    '  background: #262e61 !important;',
    '  background-color: #262e61 !important;',
    '  background-image: none !important;',
    '  color: #ffffff !important;',
    '  opacity: 1 !important;',
    '  filter: none !important;',
    '}',

    /* ---- Consent checkbox label alignment (tick + text on same line, left-aligned) ---- */
    'form[data-testid="form"] fieldset[name="emailConsent"] label {',
    '  display: flex !important;',
    '  align-items: center !important;',
    '  justify-content: flex-start !important;',
    '  gap: 8px !important;',
    '  text-align: left !important;',
    '  width: auto !important;',
    '}',
    'form[data-testid="form"] fieldset[name="emailConsent"] label > span {',
    '  text-align: left !important;',
    '  flex: 0 1 auto !important;',
    '}',
    'form[data-testid="form"] > button[data-testid="btn-form-submit"][type="submit"]::before {',
    '  content: "" !important;',
    '  position: absolute !important;',
    '  top: 0 !important;',
    '  left: -100% !important;',
    '  width: 100% !important;',
    '  height: 100% !important;',
    '  background: linear-gradient(',
    '    120deg,',
    '    transparent 0%,',
    '    rgba(109, 144, 184, 0.1) 25%,',
    '    rgba(109, 144, 184, 0.3) 50%,',
    '    rgba(109, 144, 184, 0.15) 60%,',
    '    rgba(109, 144, 184, 0.05) 75%,',
    '    transparent 100%',
    '  ) !important;',
    '  transition: none !important;',
    '  pointer-events: none !important;',
    '  z-index: 1 !important;',
    '}',
    'form[data-testid="form"] > button[data-testid="btn-form-submit"][type="submit"]:hover::before {',
    '  left: 120% !important;',
    '  transition: left 1500ms ease-out !important;',
    '}',

    /* ---- Flatten Shopify Forms outer wrappers (they force a 2-col grid w/ empty image slot) ---- */
    '[data-form-root="true"],',
    '[data-testid="app-router"],',
    'section[data-sizing="form-wrapper"],',
    '[class*="_container_"],',
    '[class*="_inline_"],',
    '[class*="_formContainer_"],',
    '[class*="_appEmbedRoot"],',
    '[class*="_appEmbed"],',
    '[class*="_gridItemContent"],',
    '[class*="_gridItem"] {',
    '  width: 100% !important;',
    '  max-width: 100% !important;',
    '  min-height: 0 !important;',
    '  --inline-container-min-height: 0 !important;',
    '  padding-top: 0 !important;',
    '  padding-bottom: 0 !important;',
    '  margin-top: 0 !important;',
    '  margin-bottom: 0 !important;',
    '}',
    /* Form-wrapper section: kill any grid template areas */
    'section[data-sizing="form-wrapper"] {',
    '  grid-template-areas: none !important;',
    '  grid-template-rows: auto !important;',
    '  row-gap: 0 !important;',
    '  column-gap: 0 !important;',
    '}',
    /* Force the form-wrapper section to single-column block layout */
    'section[data-sizing="form-wrapper"] {',
    '  display: block !important;',
    '  grid-template-columns: 1fr !important;',
    '}',
    /* Hide the empty image loading slot that reserves left half */
    '[class*="_imageLoading"] {',
    '  display: none !important;',
    '}',
    /* Left-align the privacy disclaimer text (sits below the form, after </form>) */
    '[class*="_formDisclaimer"],',
    '[class*="_formDisclaimer"] p {',
    '  text-align: left !important;',
    '}',
    /* Hide the form header entirely when it has no heading text — Shopify Forms
       still renders an empty <section class="_formHeader_*"> with empty <h2>
       which reserves vertical space above the first field. */
    '[class*="_formHeader"]:has(h2:empty) {',
    '  display: none !important;',
    '}',
    /* Make form content fill the full row */
    '[class*="_gridItemContent"] {',
    '  width: 100% !important;',
    '  max-width: 100% !important;',
    '  grid-column: 1 / -1 !important;',
    '}',

    /* ---- Form layout: 2-col AUTO-FLOW grid (form-agnostic) ----
       Single-line field containers flow naturally into 2 columns; the message
       textarea, the contact-form subject, the consent box and the submit button
       span the full row. This ONE rule set styles BOTH form structures:
         - Contact:  [Name | Email] [Subject] [Message]
         - Enquiry:  [Name | Company] [Email | Phone] [Message]
       No per-field-id targeting needed — short fields just auto-flow in DOM order. */
    'form[data-testid="form"] {',
    '  display: grid !important;',
    '  grid-template-columns: 1fr 1fr !important;',
    '  gap: 1.5rem !important;',
    '  align-items: start !important;',
    '}',
    /* Hide the invisible shop-lead-capture wrapper (first child of form) — it
       reserves a grid cell that creates a phantom gap above the first row */
    'form[data-testid="form"] > div:has(shop-lead-capture) {',
    '  display: none !important;',
    '}',
    /* Single-line field containers: auto-flow, one column each */
    'form[data-testid="form"] > [class*="_formFieldContainer"] {',
    '  grid-column: auto !important;',
    '}',
    /* Full-width exceptions: message textarea + contact-form subject */
    'form[data-testid="form"] > [class*="_formFieldContainer"]:has(textarea),',
    'form[data-testid="form"] > [class*="_formFieldContainer"]:has(input[id="custom#subject"]) {',
    '  grid-column: 1 / -1 !important;',
    '}',
    /* Consent checkbox: full width, ordered after MESSAGE */
    'form[data-testid="form"] > [class*="_consentCheckboxContainer"] {',
    '  grid-column: 1 / -1 !important;',
    '  order: 1 !important;',
    '}',
    /* Submit: full-width track but auto-width + left-aligned (matches original .button inline-flex) */
    'form[data-testid="form"] > button[data-testid="btn-form-submit"] {',
    '  grid-column: 1 / -1 !important;',
    '  order: 2 !important;',
    '  justify-self: start !important;',
    '  width: auto !important;',
    '  display: inline-flex !important;',
    '  align-items: center !important;',
    '  justify-content: center !important;',
    '}',

    /* Mobile: single column (all field containers stack) */
    '@media (max-width: 699px) {',
    '  form[data-testid="form"] {',
    '    grid-template-columns: 1fr !important;',
    '  }',
    '}',

    /* Field container holds the visual border — div has no scrollbar quirks,
       so its border renders cleanly all the way around (even for the textarea).
       Targeted by container class so it works for ANY field type (text/email/tel). */
    'form[data-testid="form"] > [class*="_formFieldContainer"] {',
    '  border: 1px solid #ddd !important;',
    '  border-radius: 4px !important;',
    '  background: #ffffff !important;',
    '  padding: 0 !important;',
    '  overflow: hidden !important;',
    '  box-shadow: none !important;',
    '  transition: border-color 0.2s ease !important;',
    '}',
    /* :focus-within fires when the input/textarea inside is focused */
    'form[data-testid="form"] > [class*="_formFieldContainer"]:focus-within {',
    '  border-color: #1a2764 !important;',
    '}',

    /* ---- Phone field: special component (country-flag selector + number input).
       Its wrapper is _formPhoneInputContainer (NOT _formFieldContainer), so it
       needs its own rules to get the unified bordered-box look.
       NOTE: overflow MUST stay visible — overflow:hidden clips the country
       dropdown list to the box height (only 1 country shows). ---- */
    'form[data-testid="form"] > [class*="_formPhoneInputContainer"] {',
    '  grid-column: auto !important;',
    '  display: flex !important;',
    '  align-items: stretch !important;',
    '  gap: 0 !important;',
    '  border: 1px solid #ddd !important;',
    '  border-radius: 4px !important;',
    '  background: #ffffff !important;',
    '  overflow: visible !important;',
    '  box-shadow: none !important;',
    '  transition: border-color 0.2s ease !important;',
    '}',
    'form[data-testid="form"] > [class*="_formPhoneInputContainer"]:focus-within {',
    '  border-color: #1a2764 !important;',
    '}',
    /* Country selector: remove only its outer border so it blends; keep a light
       divider. Do NOT touch its background or inner flag rendering. */
    'form[data-testid="form"] [class*="phone-country-selector"] {',
    '  border: none !important;',
    '  border-right: 1px solid #eee !important;',
    '}',
    /* Inner number-field container: strip its own border, fill remaining width */
    'form[data-testid="form"] [class*="_formPhoneInputContainer"] [class*="_formFieldContainer"] {',
    '  border: none !important;',
    '  border-radius: 0 !important;',
    '  background: transparent !important;',
    '  flex: 1 1 auto !important;',
    '  overflow: visible !important;',
    '}',

    /* ---- Input + textarea: borderless, transparent, sit inside the container ---- */
    'form[data-testid="form"] [class*="_formFieldContainer"] input,',
    'form[data-testid="form"] textarea {',
    '  display: block !important;',
    '  vertical-align: top !important;',
    '  border: none !important;',
    '  border-radius: 0 !important;',
    '  box-shadow: none !important;',
    '  outline: none !important;',
    '  background: transparent !important;',
    '  font-size: 1rem !important;',
    '  box-sizing: border-box !important;',
    '  width: 100% !important;',
    '  max-width: 100% !important;',
    '  -webkit-appearance: none !important;',
    '  appearance: none !important;',
    '}',
    /* Focus state on input itself — no visual change (container handles it via :focus-within) */
    'form[data-testid="form"] [class*="_formFieldContainer"] input:focus,',
    'form[data-testid="form"] textarea:focus,',
    'form[data-testid="form"] [class*="_formFieldContainer"] input:focus-visible,',
    'form[data-testid="form"] textarea:focus-visible {',
    '  outline: none !important;',
    '  box-shadow: none !important;',
    '}',
    /* Textarea-specific overrides — extra-specific selector to beat _textArea_.
       DO NOT override padding — Shopify Forms calculates padding-top to make
       room for the floating label; setting padding: 0.75rem makes typed text
       overlap with the MESSAGE label. */
    'form[data-testid="form"] textarea,',
    'form[data-testid="form"] textarea[class*="_textArea"] {',
    '  resize: none !important;',
    '  min-height: 120px !important;',
    '  font-family: inherit !important;',
    '  line-height: 1.5 !important;',
    '  overflow: auto !important;',
    '  scrollbar-gutter: stable !important;',
    '}',
    /* Hide the WebKit resizer corner explicitly (some browsers show even with resize:none) */
    'form[data-testid="form"] textarea::-webkit-resizer {',
    '  display: none !important;',
    '}',
    /* Floating label sits inside the field — give it a transparent background so it
       doesn't visually break the border line when it floats up to the top edge */
    'form[data-testid="form"] label[id^="label-"] {',
    '  background: transparent !important;',
    '  pointer-events: none !important;',
    '}',
  ].join('\n');

  /* Single-column override — applied only to forms inside a .b2b-shopify-form
     wrapper (the trade-login application form), which we want stacked one field
     per row instead of the default 2-column layout. */
  var SINGLE_COL_CSS = [
    'form[data-testid="form"] {',
    '  grid-template-columns: 1fr !important;',
    '}',
  ].join('\n');

  var INJECT_MARKER = 'data-ttp-forms-overrides';

  function inject(shadowRoot, singleColumn) {
    if (!shadowRoot) return false;
    if (shadowRoot.querySelector('style[' + INJECT_MARKER + ']')) return true;
    var style = document.createElement('style');
    style.setAttribute(INJECT_MARKER, '');
    style.textContent = SHADOW_CSS + (singleColumn ? '\n' + SINGLE_COL_CSS : '');
    shadowRoot.appendChild(style);
    return true;
  }

  function processForms() {
    var embeds = document.querySelectorAll('shopify-forms-embed');
    var allReady = embeds.length > 0;
    for (var i = 0; i < embeds.length; i++) {
      var singleColumn = !!(embeds[i].closest && embeds[i].closest('.b2b-shopify-form'));
      if (!inject(embeds[i].shadowRoot, singleColumn)) allReady = false;
    }
    return allReady;
  }

  /* Run once on load */
  processForms();

  /* Watch for forms that mount later (Shopify Forms loads async) */
  var observer = new MutationObserver(function () { processForms(); });
  observer.observe(document.body, { childList: true, subtree: true });

  /* Belt-and-braces: poll for up to 10s in case observer misses the shadow attach */
  var attempts = 0;
  var poll = setInterval(function () {
    processForms();
    if (++attempts > 20) clearInterval(poll);
  }, 500);
})();
